import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { RequestQrBatchOutcome } from '../lib/qrBatch';
import { QR_BATCHES_PREFIX } from '../lib/queryKeys';

export type RequestOutcome = RequestQrBatchOutcome & { amount: number };

export interface UseRequestQrBatch {
  request: (amount: number) => void;
  isRequesting: boolean;
  // The last answer, with the amount it answers about.
  outcome: RequestOutcome | null;
  // A failure that is not an outcome: network, 5xx, 403. Worded with fixed copy by the card.
  isError: boolean;
}

// The request AWAITS the refresh of the list and the stats, so the button is not idle before the
// new PENDING row — and with it the polling — is on screen. A refused request («pending_exists»)
// refreshes too: the screen was behind, and reading the list again is what disables the form. A
// failure refreshes from `onError` for the same reason. Never retried: a second POST is a second
// batch of labels.
export function useRequestQrBatch(
  requestQrBatch: (amount: number) => Promise<RequestQrBatchOutcome>,
  options: { onRequested?: () => void } = {},
): UseRequestQrBatch {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    retry: false,
    mutationFn: async (amount: number): Promise<RequestOutcome> => {
      const result = await requestQrBatch(amount);
      if (result.kind !== 'invalid_amount') {
        await queryClient.invalidateQueries({ queryKey: QR_BATCHES_PREFIX });
      }
      return { ...result, amount };
    },
    onSuccess: (result) => {
      if (result.kind === 'requested') options.onRequested?.();
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: QR_BATCHES_PREFIX });
    },
  });

  return {
    request: (amount) => {
      mutation.mutate(amount);
    },
    isRequesting: mutation.isPending,
    outcome: mutation.data ?? null,
    isError: mutation.isError,
  };
}
